import { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_TOKEN = process.env.REACT_APP_SIDEBAR_TOKEN || '';

// Token resolution order: ?token=… in the URL → REACT_APP_SIDEBAR_TOKEN bundled at build time.
function resolveSidebarToken() {
  if (typeof window === 'undefined') return FALLBACK_TOKEN;
  const params = new URLSearchParams(window.location.search);
  return params.get('token') || FALLBACK_TOKEN;
}

const STAGES = [
  { label: 'Prospect', color: '#94A3B8', bg: '#F1F5F9' },
  { label: 'Aware', color: '#3B82F6', bg: '#EFF6FF' },
  { label: 'Engaged', color: '#8B5CF6', bg: '#F5F3FF' },
  { label: 'Qualified', color: '#00D95F', bg: '#ECFDF5' },
  { label: 'Won', color: '#059669', bg: '#D1FAE5' },
  { label: 'Lost', color: '#EF4444', bg: '#FEF2F2' },
];

export default function ChatwootSidebarPage() {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [contactId, setContactId] = useState(null);
  const [accountId, setAccountId] = useState(1);
  const [sidebarToken] = useState(() => resolveSidebarToken());

  const fetchContact = useCallback(async (cId, aId) => {
    if (!cId) return;
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(
        `${API_URL}/api/chatwoot/sidebar/contact/${cId}?account_id=${aId}`,
        { headers: sidebarToken ? { 'X-Sidebar-Token': sidebarToken } : {} }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setContact(data);
    } catch (e) {
      setError('Could not load contact data');
    } finally {
      setLoading(false);
    }
  }, [sidebarToken]);

  // Listen for postMessage events from the parent Chatwoot frame
  useEffect(() => {
    const handler = (e) => {
      const { type, data } = e.data || {};
      if (type === 'chatwoot:set-user' && data?.id) {
        const cId = data.id;
        const aId = data.account_id || 1;
        setContactId(cId);
        setAccountId(aId);
        fetchContact(cId, aId);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [fetchContact]);

  const updateStage = async (stage) => {
    if (!contactId) return;
    setUpdating(true);
    setError('');
    setSuccess('');
    try {
      const resp = await fetch(
        `${API_URL}/api/chatwoot/sidebar/contact/${contactId}/stage`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(sidebarToken ? { 'X-Sidebar-Token': sidebarToken } : {}),
          },
          body: JSON.stringify({ stage, account_id: accountId }),
        }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setContact((c) => ({ ...c, lead_stage: stage }));
      setSuccess(`Stage updated to ${stage}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Failed to update stage');
    } finally {
      setUpdating(false);
    }
  };

  const currentStage = STAGES.find((s) => s.label === contact?.lead_stage);

  return (
    <div
      className="min-h-screen bg-white font-sans text-slate-800 p-4 flex flex-col gap-4"
      data-testid="chatwoot-sidebar"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/giri-logo.png" alt="GiriChat" className="w-5 h-5 object-contain" />
          <span className="text-xs font-bold text-slate-700 uppercase tracking-widest">
            GiriChat CRM
          </span>
        </div>
        {contactId && (
          <button
            onClick={() => fetchContact(contactId, accountId)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            data-testid="sidebar-refresh-btn"
            title="Refresh"
          >
            <RefreshCw className="w-3.5 h-3.5 text-slate-400" />
          </button>
        )}
      </div>

      {/* Waiting state */}
      {!contactId && !loading && (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-10">
          <div className="w-10 h-10 rounded-full bg-[#00D1B2]/10 flex items-center justify-center mb-3">
            <img src="/giri-logo.png" alt="" className="w-5 h-5 object-contain" />
          </div>
          <p className="text-xs text-slate-500">Open a conversation to view lead data</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-[#00D1B2]" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          {error}
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-100 rounded-lg text-xs text-green-700">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          {success}
        </div>
      )}

      {/* Contact Data */}
      {contact && !loading && (
        <>
          {/* Contact info */}
          <div className="bg-slate-50 rounded-xl p-3 space-y-1.5">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Contact</p>
            {contact.name && (
              <p className="text-sm font-semibold text-slate-900">{contact.name}</p>
            )}
            {contact.phone && (
              <p className="text-xs text-slate-500 font-mono">{contact.phone}</p>
            )}
            {contact.aniyor_seller_id && (
              <p className="text-xs text-slate-400">
                Seller: <span className="font-mono text-slate-600">{contact.aniyor_seller_id}</span>
              </p>
            )}
          </div>

          {/* Current Stage */}
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
              Lead Stage
            </p>
            {currentStage ? (
              <span
                className="inline-block text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider"
                style={{ backgroundColor: currentStage.bg, color: currentStage.color }}
                data-testid="current-stage-badge"
              >
                {currentStage.label}
              </span>
            ) : (
              <span className="text-xs text-slate-400 italic">Not set</span>
            )}
          </div>

          {/* Stage Buttons */}
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
              Update Stage
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {STAGES.map(({ label, color }) => (
                <button
                  key={label}
                  onClick={() => updateStage(label)}
                  disabled={updating || contact.lead_stage === label}
                  data-testid={`stage-btn-${label.toLowerCase()}`}
                  className="text-xs font-semibold py-1.5 px-2 rounded-lg border transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    borderColor: contact.lead_stage === label ? color : '#E2E8F0',
                    backgroundColor: contact.lead_stage === label ? `${color}15` : 'white',
                    color: contact.lead_stage === label ? color : '#64748B',
                  }}
                >
                  {updating && contact.lead_stage !== label ? (
                    <Loader2 className="w-3 h-3 animate-spin inline" />
                  ) : label}
                </button>
              ))}
            </div>
          </div>

          {/* Last Flow Data */}
          {contact.last_flow_data && (
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                Last Flow Submission
              </p>
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 font-mono break-all max-h-28 overflow-y-auto">
                {contact.last_flow_data}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
